// src/components/music/parts/MobileTrackRow.jsx

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, ShoppingCart, Plus, Download } from 'lucide-react';
import { getTypeLabel } from '../utils/badgeUtils';

export const MobileTrackRow = ({
  track,
  index,
  isCurrent,
  isPlaying,
  playQueue,
  onPlayPause,
  onPurchase,
  processingPayment,
  onAddToPlaylist,
  currentUser,
  isPurchased,
  onDownload
}) => {
  const [downloadingId, setDownloadingId] = useState(null);
  const queueIndex = playQueue.findIndex(t => t.id === track.id);
  const purchased = isPurchased && typeof isPurchased === 'function' ? isPurchased(track.id) : false;

  const handleDownload = async (track) => {
    setDownloadingId(track.id);
    try {
      await onDownload(track);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay: (index % 12) * 0.03 }}
      viewport={{ once: true }}
      className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-white transition-all duration-200 border ${
        isCurrent
          ? 'bg-[#bd9f67]/10 border-[#bd9f67]/30'
          : 'bg-[#121318]/60 border-white/5 active:bg-white/10'
      }`}
    >
      {/* Play Button */}
      <button
        onClick={() => onPlayPause(track, queueIndex)}
        className={`p-2 rounded-full transition flex-shrink-0 ${
          isCurrent && isPlaying
            ? 'bg-[#bd9f67] text-gray-950'
            : isCurrent
            ? 'bg-[#bd9f67]/30 text-[#bd9f67]'
            : 'bg-white/10 text-white/80 active:bg-[#bd9f67] active:text-gray-950'
        }`}
      >
        {isCurrent && isPlaying ? (
          <Pause size={14} fill="currentColor" />
        ) : (
          <Play size={14} fill="currentColor" className="ml-0.5" />
        )}
      </button>

      {/* Track Info */}
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm truncate ${isCurrent ? 'text-[#bd9f67]' : 'text-white/90'}`}>
          {track.name}
        </p>
        <p className="text-xs text-white/40 truncate">{track.artist || 'The Asidors'}</p>
      </div>

      {/* Type Badge */}
      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60 flex-shrink-0">
        {getTypeLabel(track.type || 'song')}
      </span>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {purchased && currentUser && (
          <button
            onClick={() => onAddToPlaylist(track)}
            className="p-1.5 rounded-full text-white/50 active:text-white active:bg-white/10 transition"
            title="Add to playlist"
          >
            <Plus size={14} />
          </button>
        )}
        {!purchased && currentUser && (
          <button
            onClick={() => onAddToPlaylist(track)}
            className="p-1.5 rounded-full text-white/50 active:text-white active:bg-white/10 transition"
            title="Add to playlist"
          >
            <Plus size={14} />
          </button>
        )}
        <button
          onClick={() => purchased ? handleDownload(track) : onPurchase(track)}
          disabled={processingPayment === track.id || downloadingId === track.id}
          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold transition ${
            purchased
              ? 'bg-[#bd9f67]/20 text-[#bd9f67] border border-[#bd9f67]/30 active:bg-[#bd9f67]/30'
              : 'bg-[#bd9f67] text-gray-950 active:bg-[#d0b07a] shadow-sm'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {purchased ? (
            <Download size={12} />
          ) : (
            <ShoppingCart size={12} />
          )}
        </button>
      </div>
    </motion.div>
  );
};
